/** Wheel deltas in lines or pages are converted to pixels with these sizes (Firefox reports notched wheels in lines). */
const LINE_HEIGHT_PX = 16;

/** Below this many pixels a wheel event is part of a trackpad stream, which is already smooth on its own. */
const SMOOTH_WHEEL_THRESHOLD_PX = 50;

/** Sub-pixel scroll positions (zoomed pages, fractional widths) must still count as "at the end". */
const EDGE_TOLERANCE_PX = 1;

export interface TabListScrollMetrics {
  orientation: "horizontal" | "vertical"
  /** `scrollLeft` for a horizontal strip, `scrollTop` for a vertical rail. */
  scrollPosition: number
  /** `scrollWidth` or `scrollHeight`. */
  scrollSize: number
  /** `clientWidth` or `clientHeight`. */
  clientSize: number
  /** Right-to-left strips run `scrollLeft` from 0 at the start down to minus the overflow at the end. */
  rtl?: boolean
}

export interface TabListWheelInput {
  deltaX: number
  deltaY: number
  deltaMode: number
  shiftKey: boolean
}

export interface TabListWheelScroll {
  /** The physical `scrollLeft` delta to apply. */
  left: number
  /** True for notched mouse wheels, false for the fine-grained pixel stream of a trackpad. */
  smooth: boolean
}

function overflowOf(metrics: TabListScrollMetrics): number {
  return Math.max(0, metrics.scrollSize - metrics.clientSize);
}

/** Whether the strip has more content than fits, in its own orientation. */
export function tabListHasOverflow(metrics: TabListScrollMetrics): boolean {
  return overflowOf(metrics) > EDGE_TOLERANCE_PX;
}

/**
 * Which physical directions the strip can still scroll to: towards the left or top (the previous button) and
 * towards the right or bottom (the next button).
 */
export function tabListScrollEdges(metrics: TabListScrollMetrics): { canScrollBack: boolean, canScrollForward: boolean } {
  const overflow = overflowOf(metrics);
  if (overflow <= EDGE_TOLERANCE_PX) {
    return { canScrollBack: false, canScrollForward: false };
  }

  const rtl = metrics.orientation === "horizontal" && metrics.rtl === true;
  const min = rtl ? -overflow : 0;
  const max = rtl ? 0 : overflow;

  return {
    canScrollBack: metrics.scrollPosition > min + EDGE_TOLERANCE_PX,
    canScrollForward: metrics.scrollPosition < max - EDGE_TOLERANCE_PX,
  };
}

function toPixels(delta: number, deltaMode: number, pageSize: number): number {
  if (deltaMode === 1) {
    return delta * LINE_HEIGHT_PX;
  }
  if (deltaMode === 2) {
    return delta * pageSize;
  }
  return delta;
}

/**
 * Maps a wheel event over a tab strip to the horizontal scroll the strip should take over, or `null` when the
 * browser should handle the event itself:
 *
 * - a vertical rail scrolls natively, including the hand-over to the page at either end;
 * - horizontal trackpad swipes and shift + wheel scroll the strip natively;
 * - a strip without overflow never scrolls;
 * - a strip already at the end the wheel points to lets the event through, so the page or dialog scrolls instead.
 *
 * `scrollPosition` may be the target of a smooth scroll still in progress, so fast wheel turns add up.
 */
export function tabListWheelScroll(event: TabListWheelInput, metrics: TabListScrollMetrics): TabListWheelScroll | null {
  if (metrics.orientation !== "horizontal" || event.shiftKey) {
    return null;
  }
  if (event.deltaY === 0 || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) {
    return null;
  }

  const overflow = overflowOf(metrics);
  if (overflow <= EDGE_TOLERANCE_PX) {
    return null;
  }

  const delta = toPixels(event.deltaY, event.deltaMode, metrics.clientSize);
  const rtl = metrics.rtl === true;
  // Progress from the start of the strip (0) to its end (overflow), whatever the writing direction.
  const progress = rtl ? -metrics.scrollPosition : metrics.scrollPosition;

  if (delta > 0 && progress >= overflow - EDGE_TOLERANCE_PX) {
    return null;
  }
  if (delta < 0 && progress <= EDGE_TOLERANCE_PX) {
    return null;
  }

  // Wheel down moves towards the end of the strip: to the right, or to the left in a right-to-left strip.
  const step = delta > 0
    ? Math.min(delta, overflow - progress)
    : Math.max(delta, -progress);

  return {
    left: rtl ? -step : step,
    smooth: event.deltaMode !== 0 || Math.abs(delta) >= SMOOTH_WHEEL_THRESHOLD_PX,
  };
}

/** `smooth`, unless the user asked the system to reduce motion. */
export function tabListScrollBehavior(smooth = true): ScrollBehavior {
  if (!smooth) {
    return "auto";
  }
  const reduceMotion = typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return reduceMotion ? "auto" : "smooth";
}
